import React, { useState, useEffect } from 'react';
import { Calendar, Users, X, Loader2, ChevronRight, Check, Info, Shield, Star, Award, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { hotelInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { captureAbandonedBooking, trackBookingInitiated } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BEDS24_PROPERTY_ID = '327426';

const BookingModal = ({ isOpen, onClose, initialDates, roomId }) => {
  const { t, language } = useLanguage();
  const [checkIn, setCheckIn] = useState(initialDates?.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialDates?.checkOut || '');
  const [guests, setGuests] = useState(initialDates?.guests || 2);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBookingEngine, setShowBookingEngine] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    if (initialDates) {
      setCheckIn(initialDates.checkIn || '');
      setCheckOut(initialDates.checkOut || '');
      setGuests(initialDates.guests || 2);
    }
  }, [initialDates]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset iframe loaded state when URL changes
  useEffect(() => {
    setIframeLoaded(false);
  }, [embedUrl]);

  const today = new Date().toISOString().split('T')[0];

  const handleSearch = async () => {
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }

    // Fire-and-forget: capture abandoned-booking candidate + GA4 event.
    // If the guest doesn't complete on Beds24, they get a recovery email 24h later.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && emailRe.test(email)) {
      captureAbandonedBooking({
        email,
        check_in: checkIn,
        check_out: checkOut,
        adults: guests,
        children: 0,
        language: language || 'en',
      });
    }
    trackBookingInitiated(null, checkIn, checkOut);

    setIsLoading(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/beds24/embed-url`, {
        params: { check_in: checkIn, check_out: checkOut, guests, ...(roomId ? { room_id: roomId } : {}) }
      });
      
      setEmbedUrl(response.data.embed_url);
      setShowBookingEngine(true);
    } catch (error) {
      console.error('Error getting booking URL:', error);
      const fallbackUrl = `https://beds24.com/book-online.php?propid=${BEDS24_PROPERTY_ID}&checkin=${checkIn.replace(/-/g, '')}&checkout=${checkOut.replace(/-/g, '')}&numadult=${guests}${roomId ? `&roomid=${roomId}` : ''}&layout=1&colorbody=ffffff&colorpanel=f8fafc&colorcontent=1e293b&colorlink=d97706&colorbuttonbg=d97706&colorbuttontext=ffffff`;
      setEmbedUrl(fallbackUrl);
      setShowBookingEngine(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowBookingEngine(false);
    setEmbedUrl('');
    setIframeLoaded(false);
    onClose();
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  if (!isOpen) return null;

  const nights = calculateNights();

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[95vh] sm:rounded-2xl sm:w-full sm:max-w-4xl lg:max-w-5xl overflow-hidden shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 p-4 sm:p-6 text-white relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -right-10 -top-10 w-40 h-40 border-4 border-white rounded-full" />
          </div>
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center backdrop-blur-sm shadow-lg">
                <span className="text-white font-serif font-bold text-base tracking-wide">UP</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-serif font-bold">{hotelInfo.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex">
                    {[...Array(4)].map((_, i) => (
                      <Star key={`bm-star-${i}`} className="w-3 h-3 fill-amber-200 text-amber-200" />
                    ))}
                  </div>
                  <span className="text-amber-100 text-xs sm:text-sm">{t('booking.secureBooking') || 'Secure Booking'}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              data-testid="close-booking-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!showBookingEngine ? (
          /* Date Selection Form - with sticky button */
          <div className="flex-1 flex flex-col min-h-0">
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('booking.selectDates') || 'Select Your Dates'}</h3>
              
              {/* Date Inputs */}
              <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Calendar className="w-4 h-4 inline mr-1.5 text-amber-600" />
                    {t('hero.checkIn') || 'Check-in'}
                  </label>
                  <Input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={today}
                    className="w-full h-12 text-base border-slate-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                    data-testid="booking-checkin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Calendar className="w-4 h-4 inline mr-1.5 text-amber-600" />
                    {t('hero.checkOut') || 'Check-out'}
                  </label>
                  <Input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn || today}
                    className="w-full h-12 text-base border-slate-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                    data-testid="booking-checkout"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Users className="w-4 h-4 inline mr-1.5 text-amber-600" />
                    {t('hero.guests') || 'Guests'}
                  </label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value))}
                    className="w-full h-12 px-3 text-base border border-slate-300 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white"
                    data-testid="booking-guests"
                  >
                    {[1, 2, 3, 4, 5, 6].map((num) => (
                      <option key={num} value={num}>{num} {num > 1 ? 'Guests' : 'Guest'}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Optional email — enables price-drop alerts + 5% comeback code if booking not completed */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  <Mail className="w-4 h-4 inline mr-1.5 text-amber-600" />
                  Email (optional — we'll save your dates)
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full h-12 text-base border-slate-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                  data-testid="booking-email"
                />
                {email && (
                  <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    We'll only email you if prices drop or you don't complete — one email max.
                  </p>
                )}
              </div>

              {/* Stay Summary */}
              {nights > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-800 font-semibold">{t('booking.yourStay') || 'Your Stay'}</p>
                      <p className="text-amber-600 text-sm">
                        {nights} {nights === 1 ? 'night' : 'nights'} · {guests} {guests === 1 ? 'guest' : 'guests'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-600 text-xs">Exact price on next step</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Trust Badges - Compact on mobile */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-slate-700">{t('booking.securePayment') || 'Secure Payment'}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-slate-700">{t('booking.instantConfirmation') || 'Instant Confirm'}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                  <Award className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <span className="text-xs text-slate-700">{t('bestPrice.badge') || 'Best Price'}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                  <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-xs text-slate-700">{t('booking.freeCancellation') || 'Free Cancel'}</span>
                </div>
              </div>

              {/* Info Box - Hidden on very small screens */}
              <div className="hidden sm:block bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium text-sm">{t('booking.realTimeAvailability') || 'Real-Time Pricing'}</p>
                    <p className="text-blue-600 text-xs">{t('booking.realTimeDesc') || 'See live prices instantly'}</p>
                  </div>
                </div>
              </div>

              {/* What's Included - Desktop only */}
              <div className="hidden sm:block mb-4">
                <h4 className="font-medium text-slate-700 mb-2 text-sm">{t('booking.whatsIncluded') || "What's Included"}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {['Free WiFi', 'Breakfast Available', 'Free Cancellation', 'Best Price Guarantee'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-slate-600 text-sm">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* STICKY BUTTON - Always visible at bottom */}
            <div className="flex-shrink-0 p-4 bg-white border-t border-slate-100 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
              <Button
                onClick={handleSearch}
                disabled={isLoading || !checkIn || !checkOut}
                className="w-full bg-amber-700 hover:bg-amber-800 active:bg-amber-800 text-white py-4 text-lg font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="search-availability-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('booking.loading') || 'Loading...'}
                  </>
                ) : (
                  <>
                    {t('booking.checkAvailabilityPrices') || 'Check Availability & Prices'}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Embedded Beds24 Booking Engine */
          <div className="flex-1 flex flex-col min-h-0">
            {/* Booking Summary Bar */}
            <div className="bg-slate-50 px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">{new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium">{new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {nights}N · {guests}G
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBookingEngine(false)}
                className="text-xs h-8"
              >
                {t('booking.changeDates') || 'Change'}
              </Button>
            </div>

            {/* Beds24 iFrame */}
            <div className="flex-1 relative bg-white" style={{ minHeight: '60vh' }}>
              {!iframeLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 text-amber-600 animate-spin mx-auto mb-3" />
                    <p className="text-slate-600">{t('booking.loadingRooms') || 'Loading rooms...'}</p>
                  </div>
                </div>
              )}
              
              <iframe
                src={embedUrl}
                title="Hotel Uma Premium Booking"
                className="absolute inset-0 w-full h-full border-0"
                allow="payment"
                onLoad={() => setIframeLoaded(true)}
                data-testid="beds24-iframe"
              />
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-4 py-3 border-t flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>{t('booking.secureByBeds24') || 'Secure booking'}</span>
                </div>
                <span className="font-medium">EUR €</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
